<template>
    <div>
        <!-- Loading Spinner -->
        <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-4 ppv2-block ppv2-text-base ppv2-text-gray-500 poppinsMedium">{{
                    $t('invoices.loading')
                }}</span>
            </div>
        </div>

        <!-- DataTable -->
        <data-table
            v-if="!loading"
            ref="dataTable"
            :items="invoices"
            :columns="tableColumns"
            :currentPage="meta.current_page"
            :totalPages="meta.last_page"
            :perPage="perPage"
            :total="meta.total"
            :enableMultiSelect="true"
            :enableDragDrop="false"
            :selectedItems="selectedInvoices"
            @page-changed="handlePageChange"
            @item-selected="handleItemSelected"
            @select-all-toggled="handleSelectAllToggled"
            @selection-changed="$emit('selection-changed', selectedInvoices.length)"
            class="ppv2-min-w-full"
        >
            <!-- Type Column -->
            <template #column-type="{ item }">
                <span
                    v-if="item.type"
                    class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3.5 ppv2-py-1.5 ppv2-rounded-[12px] ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-10 ppv2-shadow-sm ppv2-h-[30px]"
                >
                    <Percent v-if="item.type.name === 'DEPOSIT'" class="ppv2-text-black ppv2-text-opacity-80" :size="14" />
                    <FileText
                        v-else-if="item.type.name === 'INVOICE' || item.type.name === 'CORRECTIVE'"
                        class="ppv2-text-black ppv2-text-opacity-80"
                        :size="14"
                    />
                    <Undo2 v-else-if="item.type.name === 'CREDIT_NOTE'" class="ppv2-text-black ppv2-text-opacity-80" :size="14" />
                    <FileText v-else class="ppv2-text-black ppv2-text-opacity-80" :size="14" />

                    <span class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-text-opacity-80 ppv2-text-capitalize">
                        {{ getTypeLabel(item) }}
                    </span>
                </span>
                <span v-else class="ppv2-text-gray-500">-</span>
            </template>

            <!-- Statut Column — mirrors the filter pills (same logic as the tabs) -->
            <template #column-order_status="{ item }">
                <span
                    v-if="item.payment_status"
                    :class="[
                        getPaymentStatusBadgeClass(item.payment_status),
                        'ppv2-inline-flex ppv2-px-3 ppv2-py-1 ppv2-rounded-md ppv2-tracking-wide ppv2-text-capitalize',
                    ]"
                >
                    {{ getPaymentStatusLabel(item.payment_status) }}
                </span>
                <span v-else>-</span>
            </template>

            <!-- Émission Column -->
            <template #column-emission="{ item }">
                <div class="ppv2-flex ppv2-flex-col">
                    {{ formatDateShort(item.created_at) }}
                    <!--<span class="ppv2-text-xs">{{ formatTime(item.created_at) }}</span>-->
                </div>
            </template>

            <!-- Échéance Column -->
            <template #column-due_date="{ item }">
                <span
                    :class="{
                        'ppv2-text-red-500': isOverdue(item),
                        'ppv2-text-black': !isOverdue(item),
                    }"
                >
                    {{ formatDateShort(item.due_date || item.created_at) }}
                </span>
            </template>

            <!-- Client Column -->
            <template #column-customer="{ item }">
                <a
                    v-if="item.order?.user?.id && !isWalkInCustomer(item.order.user)"
                    href="#"
                    @click.prevent="openCustomerModal(item.order.user.id)"
                    class="ppv2-text-black ppv2-transition-colors ppv2-text-sm ppv2-underline hover:ppv2-text-[#7D2DBD] ppv2-cursor-pointer"
                >
                    {{ item.order.user.first_name }} {{ item.order.user.last_name }}
                </a>
                <span v-else class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: normalizeFontSize(13) }">{{
                    $t('invoices.walk_in_customer')
                }}</span>
            </template>

            <!-- Numéro Column -->
            <template #column-fiscal_number="{ item }">
                <a
                    href="#"
                    @click.prevent="openInvoice(item)"
                    class="ppv2-text-black hover:ppv2-text-[#7D2DBD] ppv2-underline ppv2-transition-colors ppv2-cursor-pointer"
                >
                    {{ item.fiscal_number }}
                </a>
            </template>

            <!-- Montant HT Column -->
            <template #column-price_ht="{ item }">
                {{ formatCurrency(item.price_ht || 0) }}
            </template>

            <!-- Montant TTC Column -->
            <template #column-price_ttc="{ item }">
                {{ formatCurrency(item.price_ttc || 0) }}
            </template>

            <!--
                E-facture : statut du cycle de vie (AFNOR XP Z12-012).
                Affiché UNIQUEMENT pour les factures réellement transmises à la plateforme
                agréée — le serveur ne renvoie `e_invoice` que dans ce cas. Les tickets de
                caisse B2C relèvent de l'e-reporting et n'ont rien à afficher ici.
            -->
            <template #column-e_invoice="{ item }">
                <div v-if="item.e_invoice" class="ppv2-flex ppv2-items-center ppv2-gap-1.5">
                    <span
                        :class="eInvoiceBadgeClass(item.e_invoice.status)"
                        class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded-md ppv2-text-xs poppinsMedium ppv2-whitespace-nowrap"
                        :title="item.e_invoice.reason || ''"
                    >
                        {{ item.e_invoice.label }}
                    </span>

                    <!-- La preuve de transmission : c'est elle qu'on produit en cas de contrôle. -->
                    <span
                        v-if="item.e_invoice.has_evidence"
                        class="ppv2-text-[10px] ppv2-text-green-600"
                        title="Facture électronique archivée — pièce à produire en cas de contrôle"
                    >
                        ✓
                    </span>
                </div>
                <span v-else class="ppv2-text-gray-300 ppv2-text-xs">—</span>
            </template>

            <!-- Actions Column -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                        :title="$t('common.actions')"
                    >
                        <MoreHorizontal :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu (portalled to <body> to escape any ancestor
                         transform/overflow/stacking context that would clip a position:fixed menu) -->
                    <div
                        v-if="activeActionMenu === item.id"
                        v-portal
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[9999] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        @click.stop
                    >
                        <button
                            type="button"
                            class="actionItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                            :style="{ fontSize: normalizeFontSize(14) }"
                            @click="onMenuAction('view-invoice', item)"
                        >
                            {{ $t('invoices.view_invoice_action') }}
                        </button>
                        <button
                            type="button"
                            class="actionItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                            :style="{ fontSize: normalizeFontSize(14) }"
                            @click="onMenuAction('view-order', item)"
                        >
                            {{ $t('invoices.view_order') }}
                        </button>
                        <button
                            type="button"
                            class="actionItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                            :style="{ fontSize: normalizeFontSize(14) }"
                            @click="onMenuAction('download', item)"
                        >
                            {{ $t('invoices.download_invoice') }}
                        </button>
                        <button
                            v-if="['INVOICE', 'DEPOSIT'].includes(item.type?.name)"
                            type="button"
                            class="actionItem actionItemDanger ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="
                                item.can_create_credit_note ? 'ppv2-cursor-pointer' : 'actionItemDisabled ppv2-cursor-not-allowed'
                            "
                            :style="{ fontSize: normalizeFontSize(14) }"
                            :disabled="!item.can_create_credit_note"
                            @click="onMenuAction('create-credit-note', item)"
                        >
                            {{ $t('invoices.create_credit_note') }}
                        </button>
                    </div>
                </div>
            </template>

            <!-- Bulk Actions Slot Passthrough -->
            <template #bulk-actions="slotProps">
                <slot name="bulk-actions" v-bind="slotProps"></slot>
            </template>
        </data-table>

        <!-- Preview Modal -->
        <invoice-preview-modal :is-open="isModalOpen" :invoice="selectedInvoiceContext" @close="closeInvoiceModal" />

        <!-- Payment-method allocation popup (same modal as order cancel), always shown and
             editable; posts to the dedicated credit-note endpoint (deposit-in-place vs
             closed-order routing is decided server-side). -->
        <CreateCreditNoteModal
            v-if="creditNoteData"
            v-model="showCreditNoteModal"
            :orderId="creditNoteOrderId"
            :invoiceId="creditNoteInvoiceId"
            :data="creditNoteData"
            submit-url="/back-office/invoices-v3/credit-note"
            @done="onCreditNoteDone"
        />

        <!-- Result feedback -->
        <RefundFlowDialog
            v-model="showResultDialog"
            :title="resultDialogSuccess ? $t('invoices.success') : $t('invoices.error')"
            :message="resultDialogMessage"
            :variant="resultDialogSuccess ? 'success' : 'danger'"
            hide-cancel
            @confirm="showResultDialog = false"
            @cancel="showResultDialog = false"
        />
    </div>
</template>

<script>
import DataTable from '../Shared/DataTable.vue'
import InvoicePreviewModal from './InvoicePreviewModal.vue'
import RefundFlowDialog from './RefundFlowDialog.vue'
import CreateCreditNoteModal from './CreateCreditNoteModal.vue'
import { eventBus } from '../Customer/eventBus'
import { MoreHorizontal, Percent, FileText, Undo2 } from 'lucide-vue'
import normalizeFontSize from '../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../utils/perPage'

export default {
    name: 'ValidatedInvoicesDataTable',
    components: {
        DataTable,
        InvoicePreviewModal,
        RefundFlowDialog,
        CreateCreditNoteModal,
        MoreHorizontal,
        Percent,
        FileText,
        Undo2,
    },
    directives: {
        // Move the element to <body> on insert so a position:fixed dropdown is never
        // clipped/covered by an ancestor's transform/overflow/stacking context. Vue removes
        // the node from its current parent (<body>) on v-if teardown, so cleanup stays correct.
        portal: {
            inserted(el) {
                document.body.appendChild(el)
            },
        },
    },
    props: {
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        typeFilter: {
            type: String,
            default: 'all',
        },
        searchQuery: {
            type: String,
            default: '',
        },
        filters: {
            // NEW: Advanced filters
            type: Object,
            default: () => ({}),
        },
        columns: {
            type: Array,
            default: () => [
                { key: 'type', label: 'Type', sortable: true },
                { key: 'order_status', label: 'Statut Commande', sortable: true },
                { key: 'emission', label: 'Émission', sortable: true },
                { key: 'due_date', label: 'Échéance', sortable: true },
                { key: 'customer', label: 'Client', sortable: true },
                { key: 'fiscal_number', label: 'Numéro', sortable: true },
                { key: 'price_ht', label: 'Montant HT', sortable: true },
                { key: 'price_ttc', label: 'Montant TTC', sortable: true },
                { key: 'e_invoice', label: 'E-facture', sortable: false },
            ],
        },
    },
    data() {
        return {
            invoices: [],
            loading: false,
            selectedInvoices: [],
            meta: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            perPage: getPerPage('invoices_v3_items_per_page', 25),
            localSearchQuery: '',
            isModalOpen: false,
            selectedInvoiceContext: null,
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            // Credit-note ("avoir") flow. The editable payment-method popup is ALWAYS shown
            // (same UX as cancelling an order); a preview call feeds it, then it executes
            // server-side (deposit-in-place vs closed-order routing decided by the backend).
            creditNotePreviewLoading: false,
            showCreditNoteModal: false,
            creditNoteData: null,
            creditNoteOrderId: null,
            creditNoteInvoiceId: null,
            showResultDialog: false,
            resultDialogSuccess: false,
            resultDialogMessage: '',
        }
    },
    computed: {
        tableColumns() {
            // Translate labels if keys exist
            const translatedColumns = this.columns.map((col) => {
                const labelKey = `invoices.columns.${col.key}`
                const translatedLabel = this.$t(labelKey)
                if (translatedLabel !== labelKey) {
                    return { ...col, label: translatedLabel }
                }
                return col
            })
            // Always show actions column
            return [...translatedColumns, { key: 'actions', label: this.$t('common.actions'), sortable: false }]
        },
    },
    watch: {
        startDate() {
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        endDate() {
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        typeFilter() {
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        filters: {
            handler() {
                this.meta.current_page = 1
                this.fetchInvoices()
            },
            deep: true,
        },
    },
    mounted() {
        this.fetchInvoices()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.closeActionMenu)
        window.addEventListener('scroll', this.closeActionMenu, true)
        window.addEventListener('resize', this.closeActionMenu)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.closeActionMenu)
        window.removeEventListener('scroll', this.closeActionMenu, true)
        window.removeEventListener('resize', this.closeActionMenu)
    },
    methods: {
        normalizeFontSize,
        isWalkInCustomer(user) {
            if (!user) return true
            const fn = (user.first_name || '').trim()
            const ln = (user.last_name || '').trim()
            return (!fn || fn === '-') && (!ln || ln === '-')
        },

        /**
         * Couleur du badge e-facture.
         *
         * Rouge et orange ne sont PAS décoratifs : ils signalent les deux seuls cas où le
         * tenant doit AGIR — rejet technique (corriger puis ré-émettre) et refus commercial
         * (émettre un avoir). Tout le reste est informatif.
         *
         * Aucun badge « Validée » : ce statut n'existe pas dans la norme, et le laisser
         * croire donnerait au tenant une preuve d'acceptation qu'il n'a pas.
         */
        eInvoiceBadgeClass(status) {
            switch (status) {
                case 'rejetee': // non-conformité technique → la facture n'existe pas légalement
                    return 'ppv2-bg-red-100 ppv2-text-red-700'
                case 'refusee': // refus commercial du client → avoir
                    return 'ppv2-bg-orange-100 ppv2-text-orange-700'
                case 'non_transmise': // bloquée par une donnée manquante → à corriger
                    return 'ppv2-bg-red-50 ppv2-text-red-600'
                case 'encaissee':
                    return 'ppv2-bg-green-100 ppv2-text-green-700'
                case 'paiement_transmis':
                    return 'ppv2-bg-green-50 ppv2-text-green-700'
                case 'approuvee':
                case 'approuvee_partiellement':
                case 'completee':
                case 'prise_en_charge':
                    return 'ppv2-bg-emerald-50 ppv2-text-emerald-700'
                case 'en_litige': // le client conteste → à traiter
                case 'suspendue':
                    return 'ppv2-bg-amber-100 ppv2-text-amber-700'
                case 'hors_perimetre': // B2C → e-reporting, ce n'est pas une anomalie
                    return 'ppv2-bg-gray-100 ppv2-text-gray-500'
                default: // transmise / deposee
                    return 'ppv2-bg-blue-50 ppv2-text-blue-700'
            }
        },

        async fetchInvoices() {
            this.loading = true
            try {
                const response = await this.axios.get('/back-office/invoices-v3/validated', {
                    params: {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        search: this.localSearchQuery || this.searchQuery,
                        typeFilter: this.typeFilter,
                        page: this.meta.current_page,
                        perPage: this.perPage,
                        ...this.filters,
                    },
                })

                const data = response.data || response
                this.invoices = data.data || []
                this.meta.current_page = parseInt(data.meta?.current_page) || 1
                this.meta.last_page = parseInt(data.meta?.last_page) || 1
                this.meta.total = parseInt(data.meta?.total) || 0

                // Emit stats to parent component if available
                if (data.stats) {
                    this.$emit('stats-updated', data.stats)
                }

                // Emit filter counts to parent component if available
                if (data.filter_counts) {
                    this.$emit('filter-counts-updated', data.filter_counts)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.invoices = []
            } finally {
                this.loading = false
            }
        },
        handlePageChange(page) {
            this.meta.current_page = page
            this.fetchInvoices()
        },
        handlePerPageChange(newPerPage) {
            this.perPage = parseInt(newPerPage)
            setPerPage('invoices_v3_items_per_page', this.perPage)
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        handleSearchUpdate(searchValue) {
            this.localSearchQuery = searchValue
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        handleItemSelected(itemId) {
            const index = this.selectedInvoices.indexOf(itemId)
            if (index > -1) {
                this.selectedInvoices.splice(index, 1)
            } else {
                this.selectedInvoices.push(itemId)
            }
            this.$emit('selection-changed', this.selectedInvoices.length)
        },
        handleSelectAllToggled() {
            if (this.selectedInvoices.length === this.invoices.length) {
                // Désélectionner tout
                this.selectedInvoices = []
            } else {
                // Sélectionner tout
                this.selectedInvoices = this.invoices.map((invoice) => invoice.id)
            }
            this.$emit('selection-changed', this.selectedInvoices.length)
        },
        reload() {
            this.meta.current_page = 1
            this.fetchInvoices()
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        formatDateShort(dateString) {
            if (!dateString) return '-'
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Date(dateString).toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
        },
        formatTime(dateString) {
            if (!dateString) return ''
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Date(dateString).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        getTypeBadgeClass(item) {
            if (item.type?.name === 'INVOICE') return 'ppv2-bg-[#00B69B] ppv2-text-white'
            if (item.type?.name === 'CREDIT_NOTE') return 'ppv2-bg-[#F93333] ppv2-text-white'
            if (item.type?.name === 'DEPOSIT') return 'ppv2-bg-[#2D9CDB] ppv2-text-white'
            return 'ppv2-bg-gray-100 ppv2-text-gray-600'
        },
        getTypeLabel(item) {
            return item.type?.label || item.type?.name || this.$t('invoices.status_labels.unknown')
        },
        getPaymentStatusBadgeClass(status) {
            // Same color taxonomy as the filter pills (InvoicesPageV3.vue getBadgeColor),
            // keyed on the status id returned by the backend (paid / overdue / upcoming / draft).
            switch (status?.id) {
                case 'paid':
                    return 'ppv2-bg-[#035B2F] ppv2-text-white'
                case 'overdue':
                    return 'ppv2-bg-[#DBA800] ppv2-text-white'
                case 'upcoming':
                    return 'ppv2-bg-[#0075FF] ppv2-text-white'
                case 'draft':
                    return 'ppv2-bg-[#9C9C9C] ppv2-text-white'
                default:
                    return 'ppv2-bg-gray-100 ppv2-text-gray-600'
            }
        },
        getPaymentStatusLabel(status) {
            // Translate via the SAME i18n keys as the filter pills (invoices.type_filters.*);
            // fall back to the backend-provided label if a key is ever missing.
            const key = `invoices.type_filters.${status?.id}`
            return this.$te(key) ? this.$t(key) : status?.label || '-'
        },
        openCustomerModal(userId) {
            if (typeof window.setOrderToModal === 'function') {
                window.setOrderToModal(userId)
            } else {
                window.open(`/back-office/v3/customer/${userId}/edit`, '_blank')
            }
        },
        openInvoice(item) {
            if (item && item.id) {
                this.selectedInvoiceContext = item
                this.isModalOpen = true
            }
        },
        closeInvoiceModal() {
            this.isModalOpen = false
            setTimeout(() => {
                this.selectedInvoiceContext = null
            }, 300)
        },
        viewInvoice(item) {
            window.location.href = `/back-office/commandes/${item.order_id}/descriptif`
        },
        async downloadInvoice(item) {
            if (!item || !item.id || !item.order_id) return
            try {
                const url = `/back-office/invoice/${item.order_id}/${item.id}`
                const response = await fetch(url)
                const blob = await response.blob()
                const blobUrl = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = `${this.$t('invoices.invoice')}_${item.fiscal_number || item.id}.pdf`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
            } catch (error) {
                const message = error.response?.data?.message || this.$t('invoices.errors.generic')
                this.$toast.error(message)
            }
        },
        // Credit-note ("avoir") flow. The server decides the mechanics from the order state
        // (zero-trust): a DEPOSIT on an open order books the avoir directly on the order with
        // negative tender rows and leaves the order untouched (not cancelled); an INVOICE on a
        // closed order goes through CancelService (negative duplicate order + negative invoices).
        // The editable payment-method popup is ALWAYS shown (same UX as cancelling an order):
        // a non-executing preview feeds it, then it posts to the credit-note endpoint.
        async createCreditNote(item) {
            if (!item || !item.order_id || !item.can_create_credit_note) return
            if (this.creditNotePreviewLoading) return
            this.creditNotePreviewLoading = true
            try {
                // item.id is the specific ValidatedOrderInvoice (the clicked deposit/invoice row);
                // the server scopes a DEPOSIT refund to THAT invoice (per-deposit, not per-order).
                const response = await this.axios.post('/back-office/invoices-v3/credit-note/preview', {
                    orderId: item.order_id,
                    invoiceId: item.id,
                })
                if (response.data && response.data.success && response.data.data) {
                    this.creditNoteOrderId = item.order_id
                    this.creditNoteInvoiceId = item.id
                    this.creditNoteData = response.data.data
                    this.showCreditNoteModal = true
                } else {
                    this.showCreditNoteError((response.data && response.data.message) || this.$t('invoices.order_cancel_error'))
                }
            } catch (error) {
                const response = error.response
                this.showCreditNoteError(
                    (response && response.data && response.data.message) || this.$t('invoices.order_cancel_error')
                )
            } finally {
                this.creditNotePreviewLoading = false
            }
        },
        onCreditNoteDone(data) {
            this.showCreditNoteModal = false
            this.creditNoteData = null
            this.creditNoteOrderId = null
            this.showCreditNoteResult(data && data.amountRefunded)
            this.fetchInvoices()
        },
        showCreditNoteResult(amountRefunded) {
            let message = this.$t('invoices.credit_note_created_success')
            if (amountRefunded && amountRefunded > 0) {
                message += `. ${this.$t('invoices.amount_refunded')}: ${this.formatCurrency(amountRefunded)}`
            }
            this.resultDialogSuccess = true
            this.resultDialogMessage = message
            this.showResultDialog = true
        },
        showCreditNoteError(message) {
            this.resultDialogSuccess = false
            this.resultDialogMessage = message
            this.showResultDialog = true
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 = min-width of dropdown
            }
        },
        closeActionMenu() {
            this.activeActionMenu = null
        },
        onMenuAction(action, item) {
            this.closeActionMenu()
            switch (action) {
                case 'view-invoice':
                    this.openInvoice(item)
                    break
                case 'view-order':
                    this.viewInvoice(item)
                    break
                case 'download':
                    this.downloadInvoice(item)
                    break
                case 'create-credit-note':
                    if (item.can_create_credit_note) {
                        this.createCreditNote(item)
                    }
                    break
            }
        },
        isOverdue(item) {
            if (!item.due_date) return false
            return new Date(item.due_date) < new Date() && item.remaining_amount > 0
        },
        stringToColor(str) {
            let hash = 0
            for (let i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash)
            }
            const c = (hash & 0x00ffffff).toString(16).toUpperCase()
            return '#' + '00000'.substring(0, 6 - c.length) + c
        },
    },
}
</script>

<style scoped lang="scss">
.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #9c9c9c;
    text-decoration: none;
    white-space: nowrap;
}

.actionItem:hover {
    background-color: #191d41;
    color: #ffffff !important;
}

.actionItemDanger {
    color: #f93333;
}

.actionItemDanger:hover {
    background-color: #f93333;
    color: #ffffff !important;
}

.actionItemDisabled,
.actionItemDisabled:hover {
    color: #d1d5db;
    background-color: transparent;
    pointer-events: none;
}
</style>
